import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import FilterHeader from "@/components/assistentes/FilterHeader";
import ScrollArea from "@/components/ui/shared/ScrollArea";
import Table from "@/components/ui/shared/table/Table";
import TableBody from "@/components/ui/shared/table/TableBody";
import TableCell from "@/components/ui/shared/table/TableCell";
import TableRow from "@/components/ui/shared/table/TableRow";
import clsx from "clsx";
import TableHeader from "@/components/ui/shared/table/TableHeader";
import { Switch } from "@/components/ui/switch";
import { useAssistants } from "@/hooks/useAssistants";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Bot, EllipsisVertical, Plus, Settings, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";

const Assistentes: React.FC = () => {
  const { t } = useTranslation("assistants");
  const navigate = useNavigate();
  const { assistants, loading, error, toggleAssistantStatus, deleteAssistant } = useAssistants();
  const [openPopoverId, setOpenPopoverId] = useState<string | null>(null);

  const handleConfigure = (id: string) => {
    setOpenPopoverId(null);
    navigate(`/assistentes/editar/${id}`);
  };

  const handleToggleStatus = async (id: string, currentStatus: boolean) => {
    await toggleAssistantStatus(id, !currentStatus);
  };

  const handleDelete = async (id: string) => {
    if (confirm(t("list.confirmDelete"))) {
      await deleteAssistant(id);
    }
  };

  if (loading) {
    return (
      <div className="w-full flex h-[calc(100dvh-120px)] flex-col">
        <div className="mb-2 pb-4 border-b border-border">
          <h2 className="text-[26px] sm:text-[30px] font-bold text-[#0F172A] dark:text-neutral-50">{t("list.title")}</h2>
        </div>
        <FilterHeader />
        <div className="flex-1 min-h-0 mt-4 flex items-center justify-center">
          <div className="flex flex-col items-center gap-4">
            <div className="w-8 h-8 border-4 border-[#E5E7EB] border-t-[#2563EB] rounded-full animate-spin dark:border-neutral-600 dark:border-t-[#60A5FA]" />
            <p className="text-muted-foreground dark:text-neutral-50">{t("list.loading")}</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full flex h-[calc(100dvh-120px)] flex-col">
        <div className="mb-2 pb-4 border-b border-border">
          <h2 className="text-[26px] sm:text-[30px] font-bold text-[#0F172A] dark:text-neutral-50">{t("list.title")}</h2>
        </div>
        <FilterHeader />
        <div className="flex-1 min-h-0 mt-4 flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4 dark:bg-red-500/20">
              <svg className="w-8 h-8 text-red-500 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L4.35 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-[#0F172A] mb-2 dark:text-neutral-50">{t("list.error")}</h3>
            <p className="text-[#64748B] text-sm dark:text-neutral-300">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full flex h-[calc(100dvh-120px)] flex-col">
      <div className="mb-2 pb-4 border-b border-border">
        <h2 className="text-[26px] sm:text-[30px] font-bold text-[#0F172A] dark:text-neutral-50">{t("list.title")}</h2>
      </div>
      <FilterHeader />
      <div className="flex-1 min-h-0 mt-4">
        {assistants.length === 0 ? (
          <div className="w-full h-full flex items-center justify-center border border-border rounded-[20px] bg-white shadow-sm dark:border-[#3F3F46] dark:rounded-[12px] dark:bg-[#27272A] dark:shadow-none">
            <div className="flex flex-col items-center gap-5 text-center p-8 max-w-md">
              <div className="w-16 h-16 bg-[#DBEAFE] rounded-full flex items-center justify-center dark:bg-blue-500/20">
                <Bot className="w-8 h-8 text-[#2563EB] dark:text-[#60A5FA]" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-[#0F172A] mb-2 dark:text-neutral-50">{t("list.empty")}</h3>
                <p className="text-[#64748B] text-sm dark:text-neutral-400">
                  {t("list.emptyHint")}
                </p>
              </div>
              <button
                onClick={() => navigate('/assistentes/novo')}
                className="flex items-center gap-2 px-5 py-2.5 bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-[12px] shadow-sm transition-colors duration-200"
              >
                <Plus size={18} />
                {t("list.create")}
              </button>
            </div>
          </div>
        ) : (
          <ScrollArea
            classNames={{
              root: "w-full h-full overflow-x-auto border border-border rounded-[20px] bg-white shadow-sm dark:border-[#3F3F46] dark:rounded-[12px] dark:bg-[#27272A] dark:shadow-none",
            }}
          >
            <Table className="min-w-[350px]">
              <TableHeader className="border-b border-border dark:border-[#3F3F46]">
                <TableRow>
                  <TableCell as="th" className="!text-[#475569] !font-semibold dark:!text-neutral-400">
                    <p className="py-2">{t("list.colAssistant")}</p>
                  </TableCell>
                  <TableCell as="th" className="!text-[#475569] !font-semibold dark:!text-neutral-400">{t("common.status")}</TableCell>
                  <TableCell as="th" className="!text-[#475569] !font-semibold dark:!text-neutral-400">{t("list.colState")}</TableCell>
                  <TableCell as="th" className="!text-[#475569] !font-semibold dark:!text-neutral-400">{t("common.actions")}</TableCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {assistants.map((assistant) => {
                  return (
                    <TableRow
                      key={assistant.id}
                      className="border-b border-border/60 last:border-b-0 hover:bg-[#F8FAFC] transition-colors duration-200 dark:border-[#3F3F46]/50 dark:hover:bg-[#1f1f23]"
                    >
                      <TableCell className="!py-4">
                        <div className="flex flex-col gap-1.5">
                          <p className="font-semibold text-[#0F172A] dark:text-neutral-50 dark:font-medium">
                            {assistant.name}
                          </p>
                          <button
                            type="button"
                            title={t("list.copyId")}
                            onClick={() => navigator.clipboard?.writeText(assistant.id)}
                            className="self-start inline-flex items-center gap-1 rounded-lg border-transparent bg-[#DBEAFE] px-2 py-0.5 font-mono text-[11px] text-[#2563EB] hover:bg-[#BFDBFE] transition-colors duration-200 dark:bg-blue-500/20 dark:text-[#60A5FA] dark:hover:bg-blue-500/30"
                          >
                            {assistant.id}
                          </button>
                        </div>
                      </TableCell>
                      <TableCell className="!py-4">
                        <div className="flex gap-1 flex-col">
                          <div className="flex justify-start items-center gap-2">
                            <div
                              className={clsx(
                                "w-[7px] h-[7px] rounded-full",
                                assistant.is_active ? "bg-[#22C55E]" : "bg-orange-400"
                              )}
                            />
                            <p className={clsx(
                              "font-medium",
                              assistant.is_active
                                ? "text-[#16A34A] dark:text-[#22C55E]"
                                : "text-orange-600 dark:text-orange-400"
                            )}>
                              {assistant.is_active ? t("list.active") : t("list.inactive")}
                            </p>
                          </div>
                          <p className="text-xs text-[#64748B] dark:text-neutral-400">
                            {t("list.maxMessages", { count: assistant.max_messages })}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell className="!py-4">
                        <Switch
                          checked={assistant.is_active}
                          onCheckedChange={() => handleToggleStatus(assistant.id, assistant.is_active)}
                          activeColor="data-[state=checked]:bg-[#16A34A] dark:data-[state=checked]:bg-[#22C55E]"
                        />
                      </TableCell>
                      <TableCell as="td" className="!py-4">
                        <Popover
                          open={openPopoverId === assistant.id}
                          onOpenChange={(open) => setOpenPopoverId(open ? assistant.id : null)}
                        >
                          <PopoverTrigger asChild>
                            <button className="w-[34px] h-[34px] flex justify-center items-center border border-border bg-white hover:bg-[#F1F5F9] rounded-[10px] transition-colors duration-200 dark:border-[#3F3F46] dark:bg-transparent dark:hover:bg-[#27272A]">
                              <EllipsisVertical size={18} className="text-[#64748B] dark:text-neutral-400" />
                            </button>
                          </PopoverTrigger>
                          <PopoverContent className="!bg-card p-1 w-[130px] flex flex-col gap-1 border-border border rounded-xl shadow-md dark:!bg-[#18181B] dark:border-[#3F3F46] dark:rounded-[7px] dark:shadow-none">
                            <button
                              onClick={() => handleConfigure(assistant.id)}
                              className="w-full flex items-center gap-2 px-3 rounded-[7px] bg-transparent hover:bg-[#F1F5F9] min-h-[32px] text-foreground transition-colors duration-200 dark:hover:bg-[#27272A] dark:text-neutral-50"
                            >
                              <Settings size={16} className="text-[#2563EB] dark:text-[#60A5FA]" />
                              {t("list.configure")}
                            </button>
                            <div className="h-px bg-border mx-1 dark:bg-[#3F3F46]" />
                            <button
                              onClick={() => {
                                setOpenPopoverId(null);
                                handleDelete(assistant.id);
                              }}
                              className="w-full flex items-center gap-2 px-3 rounded-[7px] bg-transparent hover:bg-red-50 min-h-[32px] text-red-500 transition-colors duration-200 dark:hover:bg-[#27272A] dark:text-red-400"
                            >
                              <Trash2 size={16} />
                              {t("common.remove")}
                            </button>
                          </PopoverContent>
                        </Popover>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </ScrollArea>
        )}
      </div>
    </div>
  );
};

export default Assistentes;
